import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { makeT } from "@/i18n/strings";
import { txt } from "@/lib/format";
import { applySeo, loadFonts, themeVars } from "@/lib/theme";
import { MenuHeader } from "./MenuHeader";
import { Hero } from "./Hero";
import { CategoryNav } from "./CategoryNav";
import { MenuSection } from "./MenuSection";
import { MenuFooter } from "./MenuFooter";
import { ItemModal } from "./ItemModal";
import { EmptyState, readLang } from "./MenuStates";
import { analytics, clickZone } from "@/lib/analytics";
import { useMenuTracking } from "./useMenuTracking";

const saveLang = (l) => {
  try {
    localStorage.setItem("wt_lang", l);
  } catch {}
};

function findAnchor(line) {
  for (const el of document.querySelectorAll("[data-anchor]")) {
    const r = el.getBoundingClientRect();
    if (r.bottom > line) return { id: el.dataset.anchor, top: r.top };
  }
  return null;
}

function useElementHeight(ref) {
  const [h, setH] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setH(e.target.getBoundingClientRect().height));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return h;
}

export default function MenuView({ data, mode = "public", menu = null, backHref = null }) {
  const { settings: s, categories, items } = data;
  const [chosen, setChosen] = useState(readLang);
  const lang = chosen || s.locale.default_lang;
  const dir = lang === "ar" ? "rtl" : "ltr";
  const t = useMemo(() => makeT(lang), [lang]);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [active, setActive] = useState(null);
  const [container, setContainer] = useState(null);
  const headerRef = useRef(null);
  const navRef = useRef(null);
  const headerH = useElementHeight(headerRef);
  const navH = useElementHeight(navRef);
  const anchor = useRef(null);
  const lockUntil = useRef(0);
  const lastItem = useRef(null);

  useLayoutEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    const a = anchor.current;
    anchor.current = null;
    const el = a && document.querySelector(`[data-anchor="${a.id}"]`);
    if (el) window.scrollBy(0, el.getBoundingClientRect().top - a.top);
  }, [lang, dir]);

  useEffect(() => {
    loadFonts([s.theme.font_en, s.theme.font_ar]);
  }, [s.theme.font_en, s.theme.font_ar]);

  useEffect(() => {
    applySeo(s, lang);
  }, [s, lang]);

  const stickyOffset = headerH + (s.nav.sticky ? navH : 0);

  const switchLang = useCallback(
    (l) => {
      if (l === lang) return;
      anchor.current = selectedId ? null : findAnchor(stickyOffset);
      analytics.langSwitch(l);
      setChosen(l);
      saveLang(l);
    },
    [lang, selectedId, stickyOffset]
  );

  const q = query.trim().toLocaleLowerCase();
  const byCat = useMemo(() => {
    const m = {};
    items.forEach((i) => {
      if (q && !`${i.title_en} ${i.title_ar} ${i.description_en} ${i.description_ar}`.toLocaleLowerCase().includes(q)) return;
      (m[i.category_id] ||= []).push(i);
    });
    return m;
  }, [items, q]);
  const sections = useMemo(() => categories.filter((c) => byCat[c.id]?.length), [categories, byCat]);
  const itemsById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const catsById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const currentActive = active && sections.some((c) => c.id === active) ? active : sections[0]?.id;

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (Date.now() < lockUntil.current) return;
        let cur = null;
        for (const c of sections) {
          const el = document.getElementById(`sec-${c.id}`);
          if (el && el.getBoundingClientRect().top <= stickyOffset + 24) cur = c.id;
        }
        if (cur) setActive(cur);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [sections, stickyOffset]);

  const selectCategory = useCallback(
    (id) => {
      setActive(id);
      analytics.categoryClick(id);
      lockUntil.current = Date.now() + 900;
      const el = document.getElementById(`sec-${id}`);
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - stickyOffset + 4;
      window.scrollTo({ top: y, behavior: s.animations.enabled ? "smooth" : "auto" });
    },
    [stickyOffset, s.animations.enabled]
  );

  const openItem = useCallback((id) => {
    setSelectedId(id);
    analytics.open(id, itemsById[id]?.category_id);
  }, [itemsById]);
  const closeItem = useCallback(() => {
    setSelectedId(null);
    analytics.close();
  }, []);
  const resultCount = q ? sections.reduce((n, c) => n + byCat[c.id].length, 0) : 0;
  useMenuTracking({ enabled: mode === "public", lang, active: currentActive, sections, itemsById, query, resultCount });
  const toggleSearch = useCallback(() => {
    setSearchOpen((o) => {
      if (o) setQuery("");
      return !o;
    });
  }, []);

  const selected = selectedId ? itemsById[selectedId] : null;
  if (selected) lastItem.current = selected;
  const modalItem = selected || lastItem.current;

  return (
    <div ref={setContainer} id="top" className="wt-root" dir={dir} lang={lang} style={themeVars(s, lang)} data-testid="menu-root" data-lang={lang} onClickCapture={(e) => analytics.click(clickZone(e.target))}>
      {mode === "preview" && <div className="bg-amber-400 py-1 text-center text-xs font-bold text-black" data-testid="preview-banner">{t("pub.preview")}</div>}
      {s.announcement.enabled && txt(s.announcement, "text", lang) && (
        <div data-testid="announcement-bar" className="px-4 py-2 text-center text-xs font-semibold text-white sm:text-sm" style={{ background: "var(--wt-primary)" }}>
          {txt(s.announcement, "text", lang)}
        </div>
      )}
      <div ref={headerRef} className="sticky top-0 z-40">
        <MenuHeader s={s} lang={lang} t={t} onLang={switchLang} searchOpen={searchOpen || !!query} toggleSearch={toggleSearch} query={query} setQuery={setQuery} menu={menu} backHref={backHref} />
      </div>
      <Hero s={s} lang={lang} />
      {sections.length > 0 && (
        <div ref={navRef} className={`${s.nav.sticky ? "sticky" : "relative"} z-30 mt-4`} style={{ top: s.nav.sticky ? headerH : undefined }}>
          <CategoryNav s={s} lang={lang} t={t} categories={sections} active={currentActive} onSelect={selectCategory} />
        </div>
      )}
      <main className="mx-auto min-h-[50vh] max-w-6xl px-4 pb-16 sm:px-6">
        {q && sections.length > 0 && <p className="pt-5 text-sm text-[var(--wt-muted)]" data-testid="search-result-count">{t("pub.results", { n: sections.reduce((n, c) => n + byCat[c.id].length, 0) })}</p>}
        {categories.length === 0 || items.length === 0 ? (
          <EmptyState title={t("pub.emptyMenu")} hint={t("pub.emptyMenuHint")} />
        ) : q && sections.length === 0 ? (
          <EmptyState search title={t("pub.noResults", { q: query.trim() })} hint={t("pub.noResultsHint")} onClear={() => setQuery("")} clearLabel={t("pub.clearSearch")} />
        ) : (
          sections.map((c) => <MenuSection key={c.id} category={c} items={byCat[c.id]} s={s} lang={lang} t={t} onOpen={openItem} />)
        )}
      </main>
      <MenuFooter s={s} lang={lang} t={t} />
      <ItemModal item={modalItem} category={modalItem && catsById[modalItem.category_id]} open={!!selected} onClose={closeItem} onLang={switchLang} lang={lang} s={s} t={t} container={container} />
    </div>
  );
}
